import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Divider
} from '@mui/material';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import type { InventoryItem, BranchPrice, Branch, CreateBranchPriceDto, UpdateBranchPriceDto } from '../types/inventory.types';
import { InventoryService } from '../services/inventoryService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../../common/guide/GuideButton';

interface BranchPriceModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  branches: Branch[];
  companyId: string;
  onSuccess?: () => void;
  // ✅ YENİ: BranchPrices sayfasından açıldığında kullanılacak
  preSelectedBranchId?: string | null;
  showProductSelector?: boolean; // Ürün seçici gösterilsin mi?
  onProductSelectRequest?: () => void; // Ürün seçimi isteniyor callback
}

interface EditingRow {
  id: string;
  salePrice: number;
  listPrice?: number;
  packagePrice?: number; // ✅ YENİ: Paket fiyatı
}

export const BranchPriceModal: React.FC<BranchPriceModalProps> = ({
  open,
  onClose,
  item,
  branches,
  companyId,
  onSuccess,
  preSelectedBranchId,
  showProductSelector = false,
  onProductSelectRequest
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // Auth context - fiyat değişikliği logları için kullanıcı bilgisi
  const { userDetails } = useAuth();
  
  // State
  const [branchPrices, setBranchPrices] = useState<BranchPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useBranchPricing, setUseBranchPricing] = useState(false);
  
  // Yeni fiyat ekleme
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBranchId, setNewBranchId] = useState<string | null>(null);
  const [newSalePrice, setNewSalePrice] = useState<string>('');
  const [newListPrice, setNewListPrice] = useState<string>('');
  const [newPackagePrice, setNewPackagePrice] = useState<string>(''); // ✅ YENİ
  
  // Düzenleme
  const [editingRow, setEditingRow] = useState<EditingRow | null>(null);

  // Mevcut şube fiyatları olmayan şubeleri filtrele
  const availableBranches = useMemo(() => {
    const usedBranchIds = branchPrices.map(bp => bp.branchId);
    return branches.filter(b => !usedBranchIds.includes(b.id));
  }, [branches, branchPrices]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open && item) {
      console.log('🔍 [BRANCH PRICE MODAL] item:', item);
      console.log('🔍 [BRANCH PRICE MODAL] item.useBranchPricing:', item.useBranchPricing);
      console.log('🔍 [BRANCH PRICE MODAL] typeof item.useBranchPricing:', typeof item.useBranchPricing);
      loadBranchPrices();
      setUseBranchPricing(item.useBranchPricing === true);
      
      // ✅ YENİ: preSelectedBranchId varsa formu aç ve şubeyi seç
      if (preSelectedBranchId) {
        setShowAddForm(true);
        setNewBranchId(preSelectedBranchId);
      }
    }
  }, [open, item, preSelectedBranchId]);

  // Şube fiyatlarını yükle
  // ⚠️ GÜVENLİK: companyId ile sadece kendi şirketinin verisine erişim
  const loadBranchPrices = async () => {
    if (!item || !companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const prices = await InventoryService.getBranchPrices(companyId, item.id);
      setBranchPrices(prices);
    } catch (err: any) {
      // Service 404'te boş array döner, buraya sadece gerçek hatalar gelir
      console.error('Şube fiyatları yüklenirken hata:', err);
      // Hata mesajı gösterme - API henüz deploy edilmemiş olabilir
      // setError('Şube fiyatları yüklenirken bir hata oluştu.');
      setBranchPrices([]); // Hata durumunda boş liste göster
    } finally {
      setLoading(false);
    }
  };

  // UseBranchPricing toggle
  const handleToggleUseBranchPricing = async (checked: boolean) => {
    if (!item) return;
    
    setSaving(true);
    try {
      await InventoryService.toggleUseBranchPricing(companyId, item.id, checked);
      setUseBranchPricing(checked);
      onSuccess?.();
    } catch (err: any) {
      console.error('UseBranchPricing güncellenirken hata:', err);
      setError(tt('Ayar güncellenirken bir hata oluştu.', 'An error occurred while updating settings.'));
    } finally {
      setSaving(false);
    }
  };

  // Yeni şube fiyatı ekle
  const handleAddBranchPrice = async () => {
    if (!item || !newBranchId || !newSalePrice) {
      setError(tt('Lütfen şube ve satış fiyatı seçiniz.', 'Please select branch and sale price.'));
      return;
    }
    
    const selectedBranch = branches.find(b => b.id === newBranchId);
    if (!selectedBranch) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const dto: CreateBranchPriceDto = {
        companyId,
        inventoryItemId: item.id,
        branchId: newBranchId,
        branchName: selectedBranch.name,
        salePrice: parseFloat(newSalePrice),
        saleCurrency: 'TRY',
        listPrice: newListPrice ? parseFloat(newListPrice) : undefined,
        listCurrency: newListPrice ? 'TRY' : undefined,
        packagePrice: newPackagePrice ? parseFloat(newPackagePrice) : undefined // ✅ YENİ
      };
      
      const created = await InventoryService.createBranchPrice(dto);
      setBranchPrices(prev => [...prev, created]);
      
      // Formu temizle
      setShowAddForm(false);
      setNewBranchId(null);
      setNewSalePrice('');
      setNewListPrice('');
      setNewPackagePrice(''); // ✅ YENİ
      
      onSuccess?.();
    } catch (err: any) {
      console.error('Şube fiyatı eklenirken hata:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(tt('Şube fiyatı eklenirken bir hata oluştu.', 'An error occurred while adding branch price.'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Şube fiyatını güncelle
  const handleUpdateBranchPrice = async () => {
    if (!editingRow) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // Güncellenen şube fiyatının branch bilgilerini bul
      const currentBranchPrice = branchPrices.find(bp => bp.id === editingRow.id);
      const branchInfo = branches.find(b => b.id === currentBranchPrice?.branchId);
      
      const dto: UpdateBranchPriceDto = {
        id: editingRow.id,
        companyId,
        salePrice: editingRow.salePrice,
        saleCurrency: 'TRY',
        listPrice: editingRow.listPrice,
        listCurrency: editingRow.listPrice ? 'TRY' : undefined,
        packagePrice: editingRow.packagePrice, // ✅ YENİ
        isPurchaseOnly: false,
        // 🔒 FİYAT DEĞİŞİKLİĞİ LOG BİLGİLERİ
        userId: userDetails?.id || '',
        userName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || userDetails.email : 'Unknown User',
        branchId: currentBranchPrice?.branchId || '',
        branchName: branchInfo?.name || currentBranchPrice?.branchId || ''
      };
      
      // ⚠️ GÜVENLİK: companyId ile sadece kendi şirketinin verisini güncelleyebilir
      const updated = await InventoryService.updateBranchPrice(companyId, editingRow.id, dto);
      setBranchPrices(prev => prev.map(bp => bp.id === editingRow.id ? updated : bp));
      setEditingRow(null);
      
      onSuccess?.();
    } catch (err: any) {
      console.error('Şube fiyatı güncellenirken hata:', err);
      setError(tt('Şube fiyatı güncellenirken bir hata oluştu.', 'An error occurred while updating branch price.'));
    } finally {
      setSaving(false);
    }
  };

  // Şube fiyatını sil
  // ⚠️ GÜVENLİK: companyId ile sadece kendi şirketinin verisini silebilir
  const handleDeleteBranchPrice = async (id: string) => {
    if (!window.confirm(tt('Bu şube fiyatını silmek istediğinize emin misiniz?', 'Are you sure you want to delete this branch price?'))) return;
    
    setSaving(true);
    setError(null);
    
    try {
      await InventoryService.deleteBranchPrice(companyId, id);
      setBranchPrices(prev => prev.filter(bp => bp.id !== id));
      onSuccess?.();
    } catch (err: any) {
      console.error('Şube fiyatı silinirken hata:', err);
      setError(tt('Şube fiyatı silinirken bir hata oluştu.', 'An error occurred while deleting branch price.'));
    } finally {
      setSaving(false);
    }
  };

  // Düzenlemeye başla
  const handleStartEdit = (bp: BranchPrice) => {
    setEditingRow({
      id: bp.id,
      salePrice: bp.isPurchaseOnly ? (bp.itemSalePrice ?? bp.salePrice) : bp.salePrice,
      listPrice: bp.listPrice,
      packagePrice: bp.packagePrice // ✅ YENİ
    });
  };

  // Düzenlemeyi iptal et
  const handleCancelEdit = () => {
    setEditingRow(null);
  };

  // Fiyat formatla
  const formatPrice = (price: number | undefined | null) => {
    if (price === undefined || price === null) return '-';
    return `${price.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
  };

  // ✅ YENİ: item yoksa ve showProductSelector true ise ürün seçme ekranı göster
  if (!item && showProductSelector) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StoreIcon color="primary" />
            <Typography variant="h6" component="span">
              {tt('Yeni Şube Fiyatı Ekle', 'Add New Branch Price')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4
          }}>
            <StoreIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {tt('Önce Ürün Seçin', 'Select Product First')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              {tt('Şube fiyatı eklemek için bir ürün seçmeniz gerekmektedir.', 'You need to select a product to add branch price.')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={onProductSelectRequest}
            >
              {tt('Ürün Seç', 'Select Product')}
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} variant="outlined">
            {tt('İptal', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!item) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StoreIcon color="primary" />
          <Typography variant="h6" component="span">
            {tt('Şube Bazlı Fiyat Sistemi', 'Branch Based Pricing System')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GuideButton guideKey="inventory.branchPrice" size="small" />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Ürün Bilgisi */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {item.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tt('Stok Kodu', 'Stock Code')}: {item.stockCode}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tt('Varsayılan Satış Fiyatı', 'Default Sale Price')}: {formatPrice(item.salePrice)}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={useBranchPricing}
                  onChange={(e) => handleToggleUseBranchPricing(e.target.checked)}
                  disabled={saving}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {tt('Şube Fiyatlarını Kullan', 'Use Branch Prices')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {useBranchPricing 
                      ? tt('Satışlarda şube fiyatları uygulanacak', 'Branch prices will be applied in sales') 
                      : tt('Satışlarda varsayılan fiyat kullanılacak', 'Default price will be applied in sales')}
                  </Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0 }}
            />
          </Box>
        </Paper>

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Bilgi Mesajı */}
        {!useBranchPricing && branchPrices.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt('Şube fiyatları tanımlı ancak "Şube Fiyatlarını Kullan" kapalı. Satışlarda varsayılan fiyat uygulanacak.', 'Branch prices are defined but "Use Branch Prices" is disabled. Default price will be used in sales.')}
          </Alert>
        )}

        {/* Yükleniyor */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Şube Fiyatları Tablosu */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {tt('Şube Fiyatları', 'Branch Prices')} ({branchPrices.length})
              </Typography>
              {!showAddForm && availableBranches.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddForm(true)}
                  disabled={saving}
                >
                  {tt('Yeni Ekle', 'Add New')}
                </Button>
              )}
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{tt('Şube', 'Branch')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{tt('Satış Fiyatı', 'Sale Price')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {tt('Liste Fiyatı', 'List Price')}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({tt('Opsiyonel', 'Optional')})
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {tt('Paket Fiyatı', 'Package Price')}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({tt('Opsiyonel', 'Optional')})
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', width: 120 }}>{tt('İşlemler', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Yeni Ekleme Formu */}
                  {showAddForm && (
                    <TableRow sx={{ bgcolor: 'primary.50' }}>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={availableBranches}
                          getOptionLabel={(option) => option.name}
                          value={availableBranches.find(b => b.id === newBranchId) || null}
                          onChange={(_, value) => setNewBranchId(value?.id || null)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={tt('Şube seçin...', 'Select branch...')}
                              variant="outlined"
                              size="small"
                            />
                          )}
                          sx={{ minWidth: 200 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          placeholder="0.00"
                          value={newSalePrice}
                          onChange={(e) => setNewSalePrice(e.target.value)}
                          InputProps={{
                            endAdornment: <Typography variant="caption" color="text.secondary">₺</Typography>
                          }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          placeholder={tt('Opsiyonel', 'Optional')}
                          value={newListPrice}
                          onChange={(e) => setNewListPrice(e.target.value)}
                          InputProps={{
                            endAdornment: <Typography variant="caption" color="text.secondary">₺</Typography>
                          }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          placeholder={tt('Opsiyonel', 'Optional')}
                          value={newPackagePrice}
                          onChange={(e) => setNewPackagePrice(e.target.value)}
                          InputProps={{
                            endAdornment: <Typography variant="caption" color="text.secondary">₺</Typography>
                          }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={tt('Kaydet', 'Save')}>
                          <span>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={handleAddBranchPrice}
                              disabled={saving || !newBranchId || !newSalePrice}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={tt('İptal', 'Cancel')}>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setShowAddForm(false);
                                setNewBranchId(null);
                                setNewSalePrice('');
                                setNewListPrice('');
                                setNewPackagePrice('');
                              }}
                              disabled={saving}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Mevcut Şube Fiyatları */}
                  {branchPrices.map((bp) => (
                    <TableRow key={bp.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StoreIcon fontSize="small" color="action" />
                          <Typography variant="body2">{bp.branchName || bp.branchId}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {editingRow?.id === bp.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingRow.salePrice}
                            onChange={(e) => setEditingRow({
                              ...editingRow,
                              salePrice: parseFloat(e.target.value) || 0
                            })}
                            InputProps={{
                              endAdornment: <Typography variant="caption" color="text.secondary">₺</Typography>
                            }}
                            sx={{ width: 120 }}
                          />
                        ) : bp.isPurchaseOnly ? (
                          <Tooltip title={tt('Satış fiyatı tanımlı değil (ürün kartı). Bu satırı alış faturası açtı.', 'No branch sale price (product card). Created by a purchase invoice.')}>
                            <Chip
                              size="small"
                              label={formatPrice(bp.itemSalePrice ?? bp.salePrice)}
                              variant="outlined"
                              sx={{ fontStyle: 'italic', color: 'text.disabled' }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label={formatPrice(bp.salePrice)}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingRow?.id === bp.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingRow.listPrice || ''}
                            onChange={(e) => setEditingRow({
                              ...editingRow,
                              listPrice: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            InputProps={{
                              endAdornment: <Typography variant="caption" color="text.secondary">₺</Typography>
                            }}
                            sx={{ width: 120 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {formatPrice(bp.listPrice)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingRow?.id === bp.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editingRow.packagePrice || ''}
                            onChange={(e) => setEditingRow({
                              ...editingRow,
                              packagePrice: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            InputProps={{
                              endAdornment: <Typography variant="caption" color="text.secondary">₺</Typography>
                            }}
                            sx={{ width: 120 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {formatPrice(bp.packagePrice)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editingRow?.id === bp.id ? (
                          <>
                            <Tooltip title={tt('Kaydet', 'Save')}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={handleUpdateBranchPrice}
                                  disabled={saving}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={tt('İptal', 'Cancel')}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={handleCancelEdit}
                                  disabled={saving}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title={tt('Düzenle', 'Edit')}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleStartEdit(bp)}
                                  disabled={saving || editingRow !== null}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={tt('Sil', 'Delete')}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteBranchPrice(bp.id)}
                                  disabled={saving || editingRow !== null}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Boş Durum */}
                  {branchPrices.length === 0 && !showAddForm && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {tt('Henüz şube fiyatı tanımlanmamış.', 'No branch price has been defined yet.')}
                        </Typography>
                        {availableBranches.length > 0 && (
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setShowAddForm(true)}
                            sx={{ mt: 1 }}
                          >
                            {tt('İlk şube fiyatını ekleyin', 'Add first branch price')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Şube yoksa uyarı */}
            {branches.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {tt('Henüz şube tanımlanmamış. Şube fiyatı eklemek için önce şube oluşturun.', 'No branch has been defined yet. Create a branch first to add branch prices.')}
              </Alert>
            )}

            {/* Tüm şubelere fiyat tanımlandıysa */}
            {availableBranches.length === 0 && branchPrices.length > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {tt('Tüm şubeler için fiyat tanımlanmış.', 'Prices are defined for all branches.')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined">
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchPriceModal;
